// Zmenšení fotky v prohlížeči před odesláním.
//
// Proč to musí být: originál z mobilu i z Macu má běžně 4–12 MB. Přes běžnou
// odchozí linku se takový soubor nahrává desítky sekund a hosting spojení utne
// dřív, než dorazí — uživatel pak vidí „Server je dočasně nedostupný". Zmenšená
// fotka má stovky kB a nahraje se okamžitě.
//
// `maxPx` je strop pro DELŠÍ hranu (dřív se hlídala jen šířka, takže fotka
// na výšku projde skoro nezmenšená).
//
// 257 — paměť telefonu: rozbalená fotka zabere šířka × výška × 4 B. iPhone (24–48 Mpx)
// = 100–200 MB, Xiaomi / Samsung se 108–200 Mpx = 0,4–0,8 GB na JEDNU fotku. Dřív se
// zmenšovaly tři naráz a plátna se neuvolňovala → iOS Safari i Android (MIUI hlídá paměť
// nejpřísněji) stránku shodily, nebo toBlob vrátil null a odešel originál o desítkách MB.
// Proto: (1) zmenšuje se vždy jen jedna fotka (fronta), (2) Chrome / Android dostane
// cílový rozměr rovnou při dekódování — celá fotka se do paměti vůbec nerozbalí,
// (3) plátno se po použití hned uvolní, (4) když prohlížeč plátno nevytvoří, zkusí se menší.

let queue = Promise.resolve();
// Jen jedna fotka naráz — ostatní čekají; chyba jedné frontu nezastaví.
function oneAtATime(fn) {
  const run = queue.then(fn, fn);
  queue = run.catch(() => {});
  return run;
}

// Rozměry JPEGu a EXIF orientace z prvních kB souboru — bez dekódování celé fotky.
// Vrací { w, h, orient } nebo null (není JPEG / nečitelné hlavičky).
export function jpegInfo(buf) {
  const v = new DataView(buf);
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return null;
  let off = 2, orient = 1, w = 0, h = 0;
  while (off + 4 <= v.byteLength) {
    if (v.getUint8(off) !== 0xff) { off += 1; continue; }
    const marker = v.getUint8(off + 1);
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01 || marker === 0xff) { off += marker === 0xff ? 1 : 2; continue; }
    const len = v.getUint16(off + 2);
    if (len < 2) return null;
    if (marker === 0xe1 && off + 10 <= v.byteLength && v.getUint32(off + 4) === 0x45786966) {   // „Exif"
      const t = off + 10;
      if (t + 8 <= v.byteLength) {
        const le = v.getUint16(t) === 0x4949;
        const ifd = t + v.getUint32(t + 4, le);
        if (ifd + 2 <= v.byteLength) {
          const n = v.getUint16(ifd, le);
          for (let i = 0; i < n; i++) {
            const e = ifd + 2 + i * 12;
            if (e + 12 > v.byteLength) break;
            if (v.getUint16(e, le) === 0x0112) { orient = v.getUint16(e + 8, le); break; }
          }
        }
      }
    }
    // SOF0–SOF15 kromě DHT (C4), JPG (C8) a DAC (CC)
    if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
      if (off + 9 > v.byteLength) return null;
      h = v.getUint16(off + 5); w = v.getUint16(off + 7);
      return w && h ? { w, h, orient } : null;
    }
    if (marker === 0xda) return null;   // začátek dat bez SOF
    off += 2 + len;
  }
  return null;
}

// Cílový rozměr po otočení podle EXIF (orientace 5–8 = na výšku/naležato prohozeně).
export function targetSize(info, maxPx) {
  const swap = info.orient >= 5 && info.orient <= 8;
  const W = swap ? info.h : info.w, H = swap ? info.w : info.h;
  const scale = Math.min(1, maxPx / Math.max(W, H));
  return { w: Math.max(1, Math.round(W * scale)), h: Math.max(1, Math.round(H * scale)), scale };
}

async function decode(file, maxPx) {
  // Chrome / Android: rozměr rovnou při dekódování (dekodér JPEG umí 1/2, 1/4, 1/8 —
  // 200Mpx fotka pak zabere desítky MB místo 0,8 GB). Safari volby velikosti neumí
  // a vrátí plnou fotku — to dorovná plátno níž.
  try {
    const head = await file.slice(0, 256 * 1024).arrayBuffer();
    const info = jpegInfo(head);
    if (info) {
      const t = targetSize(info, maxPx);
      if (t.scale < 1) {
        const bm = await createImageBitmap(file, { imageOrientation: "from-image", resizeWidth: t.w, resizeHeight: t.h, resizeQuality: "high" });
        // Pojistka: některé starší Chromy otáčí až PO zmenšení — fotka na výšku by vyšla
        // zdeformovaná. Nesedí-li poměr stran s originálem, načte se postaru.
        const ratio = (t.w / t.h) / (bm.width / bm.height);
        if (Math.abs(ratio - 1) < 0.02) return bm;
        bm.close?.();
      }
    }
  } catch { /* prohlížeč volby nezná nebo hlavička nesedí — klasické dekódování */ }
  // imageOrientation: bez toho se fotka z mobilu po překódování otočí naležato —
  // canvas o EXIF orientaci sám neví.
  return createImageBitmap(file, { imageOrientation: "from-image" });
}

function toJpeg(bitmap, maxPx, quality) {
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  return new Promise((resolve) => {
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
    } catch { resolve(null); }
  }).finally(() => {
    // iOS drží paměť plátna, dokud ho uklízeč neuvolní — nulový rozměr ji vrátí hned
    canvas.width = 0; canvas.height = 0;
  });
}

export function compressImage(file, maxPx = 1600, quality = 0.7) {
  if (!file || !file.type?.startsWith("image/")) return Promise.resolve(file);
  if (file.type === "image/gif") return Promise.resolve(file);   // animaci by canvas zabil
  return oneAtATime(async () => {
    let bitmap = null;
    try {
      bitmap = await decode(file, maxPx);
      let blob = await toJpeg(bitmap, maxPx, quality);
      // Došla paměť pro plátno (starší iPhone, levný Android) → menší rozměr ještě jednou
      if (!blob) { await new Promise((r) => setTimeout(r, 250)); blob = await toJpeg(bitmap, Math.min(maxPx, 1280), quality); }
      if (!blob || blob.size >= file.size) return file;   // nic jsme nezískali
      const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      return new File([blob], name, { type: "image/jpeg" });
    } catch {
      return file;   // formát, který prohlížeč nedekóduje (typicky HEIC v Chrome) — převede server (274)
    } finally {
      bitmap?.close?.();
    }
  });
}

// Soubor, který prohlížeč neumí dekódovat ani zobrazit (HEIC/HEIF). Od 274 ho při
// nahrání převádí server na JPEG — tady jen pro rozpoznání (např. náhled před nahráním).
export function isUndisplayableImage(file) {
  const name = file?.name || "";
  const type = file?.type || "";
  return /\.(heic|heif)$/i.test(name) || /image\/hei[cf]/i.test(type);
}
